/**
 * Booking an inspection: tick the step, then put it on the calendar.
 *
 * Two screens do it — the checklist's Schedule inspection box and the bottom
 * of New Lead (ADR 0228) — and this is the one sequence, so neither can drift
 * from the other. The job row already carries the inspection by the time this
 * runs; what is left is the "Inspection Scheduled" step and Google.
 *
 * The tick and the calendar are independent, and each is reported rather than
 * thrown: a tick that fails must not cost the appointment its calendar entry
 * (ADR 0040), and a calendar refusal must not undo the tick. The caller
 * decides what to show and whether to stay open.
 */

import { syncInspectionToCalendar } from "./gmail";
import { setChecklistItemStatus } from "./payments";
import { fetchChecklist, updateRow } from "./queries";

/** What `syncInspectionToCalendar` answers. */
export type CalendarResult = Awaited<
  ReturnType<typeof syncInspectionToCalendar>
>;

export interface InspectionBooking {
  /** Why the step could not be ticked, or null when it was. */
  tickError: string | null;
  calendar: CalendarResult;
}

export async function bookInspection(input: {
  jobId: string;
  /** Whether the inspection has a date. False takes it off the calendar. */
  scheduled: boolean;
  minutes: number;
  /** Ticks "Inspection Scheduled" — through whatever the screen writes with. */
  tick: () => Promise<void>;
}): Promise<InspectionBooking> {
  let tickError: string | null = null;
  try {
    await input.tick();
  } catch (e) {
    tickError = e instanceof Error ? e.message : String(e);
  }
  const calendar = await syncInspectionToCalendar({
    jobId: input.jobId,
    scheduled: input.scheduled,
    minutes: input.minutes,
  });
  return { tickError, calendar };
}

/**
 * What the calendar did, in one line for the person who just saved — or null
 * when there is nothing to say.
 */
export function calendarNote(calendar: CalendarResult): string | null {
  const left = calendar.uninvited ?? [];
  if (!calendar.ok) {
    return `Saved, but it did not reach a calendar: ${calendar.error}`;
  }
  if (left.length > 0) {
    return `On the calendar. ${left.join(" and ")} ${
      left.length > 1 ? "have" : "has"
    } no email address, so they were not invited.`;
  }
  return null;
}

/**
 * Tick "Inspection Scheduled" on a job whose checklist rows this screen has
 * never seen — New Lead, where the database wrote them with the job. The row
 * is read and written through `setChecklistItemStatus`, the one way the web
 * app changes a box (ADR 0159). A job without the row is a job the database
 * did not finish, and says so.
 */
export async function tickInspectionStep(
  jobId: string,
  profileId: string | null,
): Promise<void> {
  const step = (await fetchChecklist(jobId)).find(
    (item) =>
      item.phase === "prospect" && item.label === "Inspection Scheduled",
  );
  if (!step) {
    throw new Error("The job has no Inspection Scheduled step to tick.");
  }
  await setChecklistItemStatus(step, "done", profileId);
}

/**
 * Book the inspection New Lead just saved onto its brand-new job.
 *
 * The rep lands in the job whatever happens here, so what went wrong is left
 * where they will see it: a step that did not tick stays pending on the
 * checklist, and a calendar refusal is written to `jobs.inspection_event_error`
 * — the column calendar-sync itself writes, shown on the Overview until the
 * next save reaches Google (ADR 0040). Never throws.
 */
export async function bookNewLeadInspection(input: {
  jobId: string;
  minutes: number;
  profileId: string | null;
}): Promise<InspectionBooking> {
  const booking = await bookInspection({
    jobId: input.jobId,
    scheduled: true,
    minutes: input.minutes,
    tick: () => tickInspectionStep(input.jobId, input.profileId),
  });
  if (!booking.calendar.ok) {
    await updateRow("jobs", input.jobId, {
      inspection_event_error:
        booking.calendar.error ?? "The calendar refused it.",
    }).catch(() => {
      // The job page still shows the step; there is nowhere else to put it.
    });
  }
  return booking;
}
